import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import type { Asset, Deal } from "@/types/deal";
import { apiGet, apiGetById } from "@/services/genesisApi/httpsMethods";
import { SimpleToolbar } from "@/components/layout/Toolbar";
import { CustomButton } from "@/components/common/customButton";
import { TermSheetEditor } from "./termSheet/TermSheetEditor";

interface CreateTermSheetLocationState {
  deal?: Deal | null;
  assets?: Asset[];
}

function useCreateTermSheetData() {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const location = useLocation();
  const locationState = location.state as CreateTermSheetLocationState | null;

  const [deal, setDeal] = useState<Deal | null>(locationState?.deal ?? null);
  const [assets, setAssets] = useState<Asset[]>(locationState?.assets ?? []);
  const [loading, setLoading] = useState(
    !locationState?.deal || !locationState?.assets,
  );

  const dealId = dealIdParam ? Number(dealIdParam) : undefined;

  useEffect(() => {
    if (!dealId) return;

    const hasDealFromNavigation = Boolean(locationState?.deal);
    const hasAssetsFromNavigation = Boolean(locationState?.assets);

    if (hasDealFromNavigation && hasAssetsFromNavigation) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    void (async () => {
      setLoading(true);

      try {
        const requests: Promise<void>[] = [];

        if (!hasDealFromNavigation) {
          requests.push(
            apiGetById("deal", dealId).then((response) => {
              if (cancelled) return;
              if (response?.success && response.data?.[0]) {
                setDeal(response.data[0]);
              }
            }),
          );
        }

        if (!hasAssetsFromNavigation) {
          requests.push(
            apiGet(`assets?dealId=${dealId}`).then((response) => {
              if (cancelled) return;
              if (response?.success && Array.isArray(response.data)) {
                setAssets(response.data);
              }
            }),
          );
        }

        await Promise.all(requests);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dealId, locationState?.assets, locationState?.deal]);

  return { deal, assets, loading, dealId };
}

export default function CreateTermSheet() {
  const navigate = useNavigate();
  const { deal, assets, loading, dealId } = useCreateTermSheetData();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!deal) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Deal not found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SimpleToolbar>
        <CustomButton
          text="Back to deal"
          handleButtonPress={() => navigate(`/deals/${dealId}`)}
          isButtonDisabled={!dealId}
          solid={false}
        />
      </SimpleToolbar>

      <TermSheetEditor key={deal.id} deal={deal} assets={assets} />
    </Box>
  );
}
